/**
 * The handover note, kept ready so that signing out never has to wait for it.
 *
 * The obvious way to build a farewell is to intercept the sign-out, fetch the
 * numbers, and show them before letting go of the session. That is the wrong
 * shape here. Sign-out is a security action: if the operator has walked away,
 * or the network is slow, or the fetch throws, the session must still end at
 * the moment they pressed the button. A goodbye that can delay it is a hole.
 *
 * So the order is reversed. While the operator is signed in and the data is
 * already to hand, the assistant leaves a snapshot here. Sign-out ends the
 * session first and the gate then shows whatever snapshot exists — or no
 * farewell at all, which is a perfectly good outcome.
 *
 * Because it is a snapshot, it is stamped. "As at 14:02" is the difference
 * between a handover note and a claim about this instant, and the operator
 * reading it is about to hand the airfield to someone else.
 */

export interface SignOffSnapshot {
  name: string;
  lines: string[];
  /** When the figures were read. */
  at: number;
  /**
   * The goodbye is spoken from outside the providers — by then the session,
   * and with it the operator's preferences, are gone. So the preference
   * travels with the snapshot rather than being looked up when it is needed.
   */
  speak: boolean;
  voice?: { voiceURI?: string; rate?: number; pitch?: number };
}

let snapshot: SignOffSnapshot | null = null;

export function putSignOff(s: SignOffSnapshot): void {
  snapshot = s;
}

export function takeSignOff(): SignOffSnapshot | null {
  return snapshot;
}

/** Cleared on sign-in so one operator never reads another's handover. */
export function clearSignOff(): void {
  snapshot = null;
}
